import { escapeHtml, scalarString } from '@colplat/ui-web';
import { apiFetch, failureDetail } from '../api.js';
import { timeAgo } from './webledgerTransactionEditor.js';

/** An offline edit the server held because the record moved on (ADR-0011). */
export interface PendingRevisionDto {
  id: string;
  recordId: string;
  baseVersion: number;
  reason: string;
  createdAt: string;
  payload: { __group: true; parent: { fields: Record<string, { t: string; v: unknown }> }; children: unknown[] };
}

/** What the record says now, for the comparison. */
export interface ReviewCurrent {
  txDate?: string;
  reference?: string | null;
  description?: string | null;
  currencyCode?: string;
}

export interface ReviewBannerOptions {
  token: string;
  /** Whether asking the server is worth trying. */
  online: () => boolean;
  notify: (message: string) => void;
  current: (recordId: string) => ReviewCurrent | undefined;
  /** After a revision was applied or discarded — redraw what it touched. */
  onResolved: () => void | Promise<void>;
}

export interface ReviewBanner {
  /** Ask the server which revisions are waiting, and draw them. */
  refresh: () => Promise<void>;
}

/**
 * Offline edits the server held for review, where the person will see them.
 *
 * Moved out of the Transactions page (2026-10-21), because entries are made
 * and edited on the wallets page now and a held edit has to surface where its
 * author is. Not decoration: without it a held revision sits in a table
 * nobody reads while its author believes it was saved — worse than refusing
 * to edit offline at all (ADR-0011).
 *
 * Compare opens in place, under its own row, rather than in a dialog — the
 * banner is the same on every page that mounts it, and a page should not have
 * to carry a modal for it.
 */
export function mountReviewBanner(host: HTMLElement, opts: ReviewBannerOptions): ReviewBanner {
  let revisions: PendingRevisionDto[] = [];

  const yours = (rev: PendingRevisionDto, name: string): string =>
    scalarString((rev.payload?.parent?.fields?.[name] as { v?: unknown } | undefined)?.v);

  function compareHtml(rev: PendingRevisionDto): string {
    const now = opts.current(rev.recordId);
    const row = (label: string, theirs: string, mine: string): string =>
      `<tr><th class="cf-h">${escapeHtml(label)}</th><td>${escapeHtml(theirs)}</td>`
      + `<td class="fw-semibold">${escapeHtml(mine)}</td></tr>`;
    return `<table class="table table-sm cf-table mb-1 mt-2 w-100">
      <thead><tr><th></th><th class="cf-h">On the server now</th><th class="cf-h">Your offline version</th></tr></thead>
      <tbody>
        ${row('Date', now?.txDate ?? '—', yours(rev, 'txDate'))}
        ${row('Reference', now?.reference ?? '—', yours(rev, 'reference'))}
        ${row('Description', now?.description ?? '—', yours(rev, 'description'))}
        ${row('Currency', now?.currencyCode ?? '—', yours(rev, 'currencyCode'))}
      </tbody></table>
      <p class="text-secondary small mb-0">Applying yours replaces the whole entry, legs included — an entry is
      one document and cannot be merged line by line without risking one that does not balance.</p>`;
  }

  function render(): void {
    if (revisions.length === 0) { host.hidden = true; host.innerHTML = ''; return; }
    host.hidden = false;
    host.innerHTML = revisions.map((rev) => {
      const name = opts.current(rev.recordId)?.description || yours(rev, 'description') || 'an entry';
      return `<div class="alert alert-warning py-2 px-3 mb-2 small d-flex flex-wrap align-items-center gap-2"
          role="status" data-review-row="${escapeHtml(rev.id)}">
        <span class="flex-grow-1">
          Your offline edit to <strong>${escapeHtml(name)}</strong> could not be applied — it changed
          while you were offline (${escapeHtml(timeAgo(rev.createdAt))}). Your version is kept.
        </span>
        <button class="btn btn-sm btn-outline-secondary" type="button" data-review-compare="${escapeHtml(rev.id)}"
          aria-expanded="false">Compare</button>
        <button class="btn btn-sm btn-primary" type="button" data-review-apply="${escapeHtml(rev.id)}">Apply mine</button>
        <button class="btn btn-sm btn-link" type="button" data-review-discard="${escapeHtml(rev.id)}">Discard mine</button>
        <div class="w-100" data-review-compared hidden></div>
      </div>`;
    }).join('');
  }

  /**
   * A failure goes in the row it belongs to, beside the button pressed:
   * the banner stays standing on failure, so without this the buttons simply
   * stopped responding — and the held edit is exactly what its author cannot
   * afford to believe was handled when it was not.
   */
  function showError(id: string, message: string): void {
    const row = host.querySelector<HTMLElement>(`[data-review-row="${CSS.escape(id)}"]`);
    if (!row) return;
    let el = row.querySelector<HTMLElement>('.wl-review-error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'wl-review-error w-100 fw-semibold';
      row.appendChild(el);
    }
    el.innerHTML = `<span aria-hidden="true">⚠</span> ${escapeHtml(message)}`;
  }

  async function resolve(id: string, resolution: 'applied' | 'discarded'): Promise<void> {
    const res = await apiFetch(`/apps/webledger/pending-revisions/${id}`, {
      method: 'POST', token: opts.token, body: { resolution },
    }).catch(() => undefined);
    if (!res?.ok) {
      const message = await failureDetail(res, 'Could not resolve that edit');
      opts.notify(message);
      showError(id, message);
      return;
    }
    opts.notify(resolution === 'applied' ? 'Your version has been applied.' : 'Your version has been discarded.');
    await refresh();
    await opts.onResolved();
  }

  host.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const apply = target.closest<HTMLElement>('[data-review-apply]');
    if (apply) { void resolve(apply.dataset['reviewApply']!, 'applied'); return; }
    const discard = target.closest<HTMLElement>('[data-review-discard]');
    if (discard) { void resolve(discard.dataset['reviewDiscard']!, 'discarded'); return; }
    const compare = target.closest<HTMLElement>('[data-review-compare]');
    if (compare) {
      const rev = revisions.find((r) => r.id === compare.dataset['reviewCompare']);
      const box = compare.closest('[data-review-row]')?.querySelector<HTMLElement>('[data-review-compared]');
      if (!rev || !box) return;
      const open = box.hidden;
      if (open) box.innerHTML = compareHtml(rev);
      box.hidden = !open;
      compare.setAttribute('aria-expanded', String(open));
    }
  });

  async function refresh(): Promise<void> {
    if (!opts.online()) return;
    const res = await apiFetch('/apps/webledger/pending-revisions', { token: opts.token }).catch(() => undefined);
    if (!res?.ok) return;
    revisions = ((await res.json()) as { revisions: PendingRevisionDto[] }).revisions;
    render();
  }

  return { refresh };
}
